import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalysisStore } from '../stores/useAnalysisStore';
import { useOrderStore } from '../stores/useOrderStore';
import { useUIStore } from '../stores/useUIStore';
import { downloadExport } from '@shipping/core/api-client';
import { COURIERS, DIMENSION_CONFIG } from '@shipping/core';
import type { RoutingDimension, CourierId } from '@shipping/core';

// ---------------------------------------------------------------------------
// Risk-level label / badge-color lookup
// ---------------------------------------------------------------------------

const RISK_LABELS: Record<string, string> = {
  critical: '极高危',
  high: '高危',
  medium: '中危',
  low: '低危',
  safe: '安全',
};

const RISK_BADGE_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-blue-100 text-blue-700',
  safe: 'bg-green-100 text-green-700',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ExportPage() {
  const navigate = useNavigate();

  // Analysis stores
  const macro = useAnalysisStore((s) => s.macro);
  const micro = useAnalysisStore((s) => s.micro);
  const stationAlerts = useAnalysisStore((s) => s.stationAlerts);
  const selectedDimension = useAnalysisStore((s) => s.selectedDimension);
  const setSelectedDimension = useAnalysisStore((s) => s.setSelectedDimension);

  // Order store
  const parsedOrders = useOrderStore((s) => s.parsedOrders);

  // Task ID for backend export
  const currentTaskId = useUIStore((s) => s.currentTaskId);

  const [exportFormat, setExportFormat] = useState<'xlsx' | 'csv'>('xlsx');
  const [isExporting, setIsExporting] = useState(false);

  // -------------------------------------------------------------------------
  // Derived data: station-alert lookup by orderId
  // -------------------------------------------------------------------------
  const alertsByOrderId = useMemo(() => {
    const map = new Map<string, (typeof stationAlerts)[number]>();
    for (const alert of stationAlerts) {
      map.set(alert.orderId, alert);
    }
    return map;
  }, [stationAlerts]);

  // -------------------------------------------------------------------------
  // Derived data: parsedOrder lookup by id
  // -------------------------------------------------------------------------
  const ordersById = useMemo(() => {
    const map = new Map<string, (typeof parsedOrders)[number]>();
    for (const order of parsedOrders) {
      map.set(order.id, order);
    }
    return map;
  }, [parsedOrders]);

  // -------------------------------------------------------------------------
  // Preview rows (first 5)
  // -------------------------------------------------------------------------
  const previewRows = useMemo(() => {
    if (!micro) return [];
    return micro.results.slice(0, 5).map((result) => {
      const order = ordersById.get(result.orderId);
      const alert = alertsByOrderId.get(result.orderId);
      const recCourier = result.recommendations[selectedDimension];
      const saving = result.savings[selectedDimension];
      return {
        address: order
          ? order.destination.raw.length > 20
            ? order.destination.raw.slice(0, 20) + '...'
            : order.destination.raw
          : '—',
        weight: order ? `${order.weight}kg` : '—',
        category: order?.category ?? '—',
        currentCourier: order?.currentCourier
          ? COURIERS[order.currentCourier as CourierId]?.name ?? order.currentCourier
          : '—',
        recommendedCourier: COURIERS[recCourier]?.name ?? recCourier,
        savings: saving > 0 ? `¥${saving.toFixed(1)}` : '—',
        riskLevel: alert?.riskLevel ?? 'safe',
        rationale: result.rationale,
      };
    });
  }, [micro, ordersById, alertsByOrderId, selectedDimension]);

  // -------------------------------------------------------------------------
  // Negotiation cards — computed from real data
  // -------------------------------------------------------------------------
  const negotiationCards = useMemo(() => {
    if (!macro || !micro) return [];

    // Card 1: average weight from parsedOrders
    const avgWeight =
      parsedOrders.length > 0
        ? (parsedOrders.reduce((sum, o) => sum + o.weight, 0) / parsedOrders.length).toFixed(1)
        : '—';

    // Card 3: percentage of orders under 3kg from weightDistribution
    const lightBuckets = macro.weightDistribution.filter((b) => b.max <= 3);
    const lightRatio = lightBuckets.reduce((sum, b) => sum + b.ratio, 0);
    const lightPct = `${(lightRatio * 100).toFixed(0)}%`;

    // Card 4: top 3 champion courier names
    const championEntries = Object.values(micro.champions);
    const sortedChampions = [...championEntries].sort((a, b) => b.orderCount - a.orderCount);
    const top3Names = sortedChampions
      .slice(0, 3)
      .map((c) => COURIERS[c.courierId as CourierId]?.name ?? c.courierId)
      .join('/');

    return [
      {
        title: '月均单量优势',
        stat: `${macro.totalOrders.toLocaleString()} 单`,
        detail: `平均重量 ${avgWeight}kg`,
        pitch: '我们有稳定的中等规模单量，值得给更好的价格',
      },
      {
        title: '北上广深占比',
        stat: `${(macro.bsgdRatio * 100).toFixed(1)}%`,
        detail: `月均溢价 ¥${macro.hiddenSurcharge.toLocaleString()}`,
        pitch: '北上广深溢价占总运费的15%，希望专项优惠',
      },
      {
        title: '重量分布优势',
        stat: `${lightPct} < 3kg`,
        detail: '轻货占绝对多数',
        pitch: '轻货比例高，损耗率低，有利于谈更好价格',
      },
      {
        title: '竞品比价数据',
        stat: '7 家快递',
        detail: top3Names || '—',
        pitch: '我们有多家快递的详细比价数据作为谈判依据',
      },
    ];
  }, [macro, micro, parsedOrders]);

  // -------------------------------------------------------------------------
  // Analysis summary — computed from real data
  // -------------------------------------------------------------------------
  const summary = useMemo(() => {
    if (!macro || !micro) return null;

    const highRiskAlerts = stationAlerts.filter(
      (a) => a.riskLevel === 'critical' || a.riskLevel === 'high',
    );
    const totalFineAvoidance = highRiskAlerts.reduce((sum, a) => sum + a.potentialFine, 0);
    const annualSavings = (micro.estimatedSavings + totalFineAvoidance) * 12;

    return {
      totalOrders: macro.totalOrders,
      suggestedSwaps: micro.suggestedSwaps,
      swapRatioPct: (micro.swapRatio * 100).toFixed(1),
      estimatedSavings: micro.estimatedSavings,
      riskOrderCount: highRiskAlerts.length,
      totalFineAvoidance,
      annualSavings,
    };
  }, [macro, micro, stationAlerts]);

  // -------------------------------------------------------------------------
  // Export handler — backend-generated Excel / CSV download
  // -------------------------------------------------------------------------
  const handleExport = async () => {
    if (!micro || !currentTaskId) return;
    setIsExporting(true);
    try {
      const blob = await downloadExport(currentTaskId, {
        format: exportFormat,
        dimension: selectedDimension,
        includeRationale: true,
      });

      const today = new Date().toISOString().slice(0, 10);
      const ext = exportFormat === 'xlsx' ? 'xlsx' : 'csv';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `发货策略_${today}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('导出失败，请重试');
    } finally {
      setIsExporting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Empty state — no analysis data
  // -------------------------------------------------------------------------
  if (!micro || !macro) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <div className="text-6xl mb-4">📥</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">暂无分析数据</h2>
        <p className="text-sm text-gray-500 mb-6">请先上传订单数据并完成分析</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2.5 bg-brand text-white font-medium rounded-lg hover:bg-blue-600 transition-colors"
        >
          前往上传
        </button>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">导出与策略交付</h1>
        <p className="text-sm text-gray-500 mt-1">
          下载增强版订单表 + 谈判筹码卡片
        </p>
      </div>

      {/* Export section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <h2 className="text-lg font-medium text-gray-900">策略明细表导出</h2>

        <div className="text-sm text-gray-600 space-y-1">
          <p>导出内容：原始数据 + 4 个增强列：</p>
          <ul className="list-none space-y-1 ml-2">
            <li className="flex items-center gap-2">
              <span className="text-success">✓</span> 推荐快递（{DIMENSION_CONFIG[selectedDimension].label}）
            </li>
            <li className="flex items-center gap-2">
              <span className="text-success">✓</span> 预计节省金额
            </li>
            <li className="flex items-center gap-2">
              <span className="text-success">✓</span> 驿站风险预警
            </li>
            <li className="flex items-center gap-2">
              <span className="text-success">✓</span> 决策理由
            </li>
          </ul>
        </div>

        <div className="flex flex-wrap gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              导出格式
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setExportFormat('xlsx')}
                className={[
                  'px-4 py-2 text-sm rounded-md border transition-colors',
                  exportFormat === 'xlsx'
                    ? 'border-brand bg-blue-50 text-brand'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                ].join(' ')}
              >
                Excel (.xlsx)
              </button>
              <button
                onClick={() => setExportFormat('csv')}
                className={[
                  'px-4 py-2 text-sm rounded-md border transition-colors',
                  exportFormat === 'csv'
                    ? 'border-brand bg-blue-50 text-brand'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                ].join(' ')}
              >
                CSV
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              推荐维度
            </label>
            <select
              value={selectedDimension}
              onChange={(e) => setSelectedDimension(e.target.value as RoutingDimension)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md"
            >
              <option value="composite">综合最优</option>
              <option value="speed">速度优先</option>
              <option value="price">价格优先</option>
              <option value="rating">评价优先</option>
            </select>
          </div>
        </div>

        {/* Preview table */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            表格预览（前 5 行）
          </h3>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    收货地址
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">
                    重量
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    类目
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    当前快递
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-brand">
                    推荐快递
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-brand">
                    节省
                  </th>
                  <th className="px-3 py-2 text-center font-medium text-brand">
                    驿站风险
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-brand">
                    理由
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {previewRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-4 text-center text-gray-400">
                      暂无数据
                    </td>
                  </tr>
                ) : (
                  previewRows.map((row, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 text-gray-700 max-w-[180px] truncate">
                        {row.address}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700">{row.weight}</td>
                      <td className="px-3 py-2 text-gray-700">{row.category}</td>
                      <td className="px-3 py-2 text-gray-700">{row.currentCourier}</td>
                      <td className="px-3 py-2 font-medium">{row.recommendedCourier}</td>
                      <td className="px-3 py-2 text-right text-success">
                        {row.savings}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`inline-block px-2 py-0.5 text-xs rounded ${
                            RISK_BADGE_COLORS[row.riskLevel] ?? RISK_BADGE_COLORS.safe
                          }`}
                        >
                          {RISK_LABELS[row.riskLevel] ?? '安全'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-500 text-xs max-w-[200px] truncate">
                        {row.rationale}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <button
          onClick={handleExport}
          disabled={isExporting || !currentTaskId}
          className="inline-flex items-center gap-2 px-8 py-3 bg-brand text-white font-medium rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
        >
          {isExporting ? (
            <>
              <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              导出中...
            </>
          ) : (
            '下载策略明细表'
          )}
        </button>
      </div>

      {/* Negotiation cards */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <div>
          <h2 className="text-lg font-medium text-gray-900">谈判筹码卡片</h2>
          <p className="text-sm text-gray-500 mt-1">
            用于与快递公司谈判时展示数据支撑
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {negotiationCards.map((card, i) => (
            <div
              key={i}
              className="border border-gray-200 rounded-lg p-5 hover:shadow-sm transition-shadow"
            >
              <div className="text-sm text-gray-500">谈判卡 #{i + 1}</div>
              <div className="text-lg font-semibold text-gray-900 mt-1">
                {card.title}
              </div>
              <div className="text-2xl font-bold text-brand mt-2">{card.stat}</div>
              <div className="text-sm text-gray-400">{card.detail}</div>
              <p className="text-sm text-gray-600 mt-3 italic">"{card.pitch}"</p>
            </div>
          ))}
        </div>
      </div>

      {/* Analysis summary */}
      {summary && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-3">本次分析概要</h2>
          <ul className="space-y-1.5 text-sm text-gray-700">
            <li>
              分析订单:{' '}
              <span className="font-semibold">
                {summary.totalOrders.toLocaleString()} 单
              </span>
            </li>
            <li>
              建议调整快递:{' '}
              <span className="font-semibold text-warning">
                {summary.suggestedSwaps.toLocaleString()} 单 ({summary.swapRatioPct}%)
              </span>
            </li>
            <li>
              预计月度节省:{' '}
              <span className="font-semibold text-success">
                ¥{summary.estimatedSavings.toLocaleString()}
              </span>
            </li>
            <li>
              规避驿站风险:{' '}
              <span className="font-semibold">{summary.riskOrderCount} 单</span>，避免罚款{' '}
              <span className="text-danger font-semibold">
                ¥{summary.totalFineAvoidance.toLocaleString()}
              </span>
            </li>
            <li>
              综合年化节省:{' '}
              <span className="font-semibold text-success text-lg">
                ¥{summary.annualSavings.toLocaleString()}
              </span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
